import { useEffect, useState } from "react";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { ProjectionChart } from "@/components/Charts";
import { fmtMoney, fmtPctPlain } from "@/lib/format";
import { Play, TrendUp } from "@phosphor-icons/react";

const PRESETS = {
    Base:     { revenue_growth_pct: 5,   cost_inflation_pct: 3,   capex_intensity_pct: 6 },
    Upside:   { revenue_growth_pct: 10,  cost_inflation_pct: 2.5, capex_intensity_pct: 7 },
    Downside: { revenue_growth_pct: -3,  cost_inflation_pct: 5,   capex_intensity_pct: 4 },
};

export default function Planning() {
    const [meta, setMeta] = useState(null);
    const [scope, setScope] = useState("group");
    const [rev, setRev] = useState(5);
    const [cost, setCost] = useState(3);
    const [capex, setCapex] = useState(6);
    const [horizon, setHorizon] = useState(12);
    const [result, setResult] = useState(null);
    const [baseResult, setBaseResult] = useState(null);
    const [preset, setPreset] = useState("Base");
    const [running, setRunning] = useState(false);

    useEffect(() => { client.get("/meta/hierarchy").then((r) => setMeta(r.data)); }, []);

    const applyPreset = (p) => {
        setPreset(p);
        setRev(PRESETS[p].revenue_growth_pct);
        setCost(PRESETS[p].cost_inflation_pct);
        setCapex(PRESETS[p].capex_intensity_pct);
    };

    const run = async () => {
        setRunning(true);
        try {
            const [selected, base] = await Promise.all([
                client.post("/planning/scenario", { scope, revenue_growth_pct: parseFloat(rev), cost_inflation_pct: parseFloat(cost), capex_intensity_pct: parseFloat(capex), horizon_months: parseInt(horizon) }),
                client.post("/planning/scenario", { scope, ...PRESETS.Base, horizon_months: parseInt(horizon) }),
            ]);
            setResult(selected.data);
            setBaseResult(base.data);
        } finally { setRunning(false); }
    };

    useEffect(() => { if (meta) run(); }, [meta]); // eslint-disable-line

    return (
        <div>
            <PageHeader
                overline="Planning · Budgeting · Forecasting"
                title="Scenario Workbench"
                description="Model driver-based projections across the group, a sector or a country. Adjust growth, cost inflation and capex to test forward paths."
            />

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                {/* Driver panel */}
                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="scenario-drivers">
                    <div className="flex items-baseline justify-between mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted">Drivers · Presets</div>
                        <div className="flex gap-2" data-testid="scenario-presets">
                            {Object.keys(PRESETS).map((p) => (
                                <button key={p} data-testid={`preset-${p}`} onClick={() => applyPreset(p)} className={`px-3 py-1 font-sans text-xs ${preset === p ? "bg-fp-ink text-white" : "border border-fp-border text-fp-secondary hover:border-fp-ink"}`}>{p}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
                        <div>
                            <label className="block font-sans text-xs text-fp-secondary mb-2">Scope</label>
                            <select data-testid="scenario-scope" value={scope} onChange={(e) => setScope(e.target.value)} className="w-full border-b border-fp-border bg-transparent pb-2 pt-1 focus:outline-none focus:border-fp-ink font-sans text-sm text-fp-ink">
                                <option value="group">Group</option>
                                <optgroup label="Sector">
                                    {meta?.sectors?.map((s) => <option key={s.id} value={`sector:${s.id}`}>{s.name}</option>)}
                                </optgroup>
                                <optgroup label="Country">
                                    {meta?.countries?.map((c) => <option key={c.code} value={`country:${c.code}`}>{c.name}</option>)}
                                </optgroup>
                            </select>
                        </div>
                        <DriverInput testId="driver-revenue" label="Revenue Growth (%/yr)" value={rev} onChange={setRev} min={-20} max={40} step={0.5} />
                        <DriverInput testId="driver-cost" label="Cost Inflation (%/yr)" value={cost} onChange={setCost} min={0} max={20} step={0.5} />
                        <DriverInput testId="driver-capex" label="CapEx Intensity (% Rev)" value={capex} onChange={setCapex} min={0} max={25} step={0.5} />
                        <div>
                            <label className="block font-sans text-xs text-fp-secondary mb-2">Horizon (months)</label>
                            <select data-testid="scenario-horizon" value={horizon} onChange={(e) => setHorizon(e.target.value)} className="w-full border-b border-fp-border bg-transparent pb-2 pt-1 focus:outline-none focus:border-fp-ink font-sans text-sm text-fp-ink">
                                <option value={6}>6 months</option>
                                <option value={12}>12 months</option>
                                <option value={24}>24 months</option>
                                <option value={36}>36 months</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-8">
                        <button data-testid="run-scenario-btn" onClick={run} disabled={running} className="inline-flex items-center gap-2 bg-fp-ink text-white px-6 py-2.5 font-sans text-sm hover:bg-fp-ink-hover transition-colors disabled:opacity-50">
                            <Play size={14} weight="fill" />
                            {running ? "Projecting…" : "Run Scenario"}
                        </button>
                    </div>
                </div>

                {result && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="scenario-summary">
                            <KpiCard label="Projected Revenue" value={fmtMoney(result.totals.revenue_musd, { decimals: 0 })} subValue={`${result.projection.length}-mo horizon`} />
                            <KpiCard label="Projected EBITDA" value={fmtMoney(result.totals.ebitda_musd, { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(result.totals.ebitda_margin_pct)}`} />
                            <KpiCard label="Projected FCF" value={fmtMoney(result.totals.free_cash_flow_musd, { decimals: 0 })} />
                            <KpiCard label="Baseline Rev." value={fmtMoney(result.baseline_last_actual_revenue_musd, { decimals: 0 })} subValue="Last actual month" />
                        </div>

                        <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                            <div className="mb-6">
                                <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">{result.scope_label}</div>
                                <h3 className="font-serif text-2xl text-fp-ink">Forward Projection</h3>
                            </div>
                            <ProjectionChart projection={result.projection} baseline={result.baseline_last_actual_revenue_musd} />
                        </div>

                        <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                            <div className="mb-4">
                                <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Monthly Detail</div>
                                <h3 className="font-serif text-2xl text-fp-ink">Projected Line Items</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b-2 border-fp-ink">
                                            <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Month</th>
                                            <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Revenue</th>
                                            <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">EBITDA</th>
                                            <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Margin</th>
                                            <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">FCF</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.projection.map((p) => (
                                            <tr key={p.month_index} className="border-b border-fp-border">
                                                <td className="py-2.5 font-mono text-xs text-fp-secondary">M+{p.month_index}</td>
                                                <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(p.revenue_musd)}</td>
                                                <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(p.ebitda_musd)}</td>
                                                <td className="py-2.5 text-right font-mono text-sm text-fp-secondary">{fmtPctPlain(p.ebitda_margin_pct)}</td>
                                                <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(p.free_cash_flow_musd)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const DriverInput = ({ label, value, onChange, min, max, step, testId }) => (
    <div>
        <label className="block font-sans text-xs text-fp-secondary mb-2">{label}</label>
        <div className="flex items-baseline gap-2">
            <input
                data-testid={testId}
                type="number" value={value} step={step} min={min} max={max}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border-b border-fp-border bg-transparent pb-2 pt-1 focus:outline-none focus:border-fp-ink font-mono text-lg text-fp-ink"
            />
            <span className="font-mono text-xs text-fp-muted">%</span>
        </div>
    </div>
);
